// Perfumes: las listas, sus nombres en pantalla y las cuentas chicas
// que usan varias pantallas (22/9).
//
// ⚠️  SE LLAMA `perfumesDatos.js` Y NO `perfumes.js`, Y NO ES GUSTO:
//     al lado vive `Perfumes.js` (la pantalla), y en Windows —donde
//     está el repositorio— dos nombres que solo difieren en mayúscula
//     son EL MISMO archivo. Copiar uno pisaba al otro sin avisar. Lo
//     detectó el build («multiple modules with names that only differ
//     in casing»), no una persona.
//
// ⚠️  LAS LISTAS SON LAS MISMAS DE `lib/perfumes.js` Y DE LOS `check` DE
//     `supabase/perfumes.sql`. `test_perfumes.js` §1 lee los tres
//     archivos y exige que coincidan: una familia que exista acá y no en
//     la base daría un error al guardar que el usuario no puede
//     entender.
//
// Lo que se guarda es el CÓDIGO (`edp`, `amaderado`). Lo que se lee es
// la traducción, que sale de `perfumes.concentracion.edp` y compañía en
// los doce idiomas. El español viaja acá como respaldo, igual que en
// todo el resto de la app.

import { t } from "./i18n";

export const CONCENTRACIONES = ["colonia", "edt", "edp", "parfum"];
export const FAMILIAS = [
  "citrico",
  "fresco",
  "aromatico",
  "amaderado",
  "ambar",
  "floral",
  "chipre",
  "gourmand",
  "cuero",
];
export const MOMENTOS = ["dia", "noche", "ambos"];

/* LOS NOMBRES, CON EL ESPAÑOL ADENTRO DE CADA t().
 *
 * La primera versión tenía una tabla `{ colonia: "Colonia", … }` y
 * llamaba `t(\`perfumes.concentracion.${v}\`, TABLA[v])`. Dos guardianes
 * la rechazaron, y con razón:
 *
 *   · `test_textos_crudos.js` ve español suelto en la tabla: no sabe que
 *     cada valor termina siendo respaldo de un t().
 *   · `test_i18n.js` §6c, con la clave dinámica, toma como comodín todo
 *     lo que va antes del `${`: una clave de perfumes que quedara
 *     huérfana ya no se detectaría.
 *
 * Una llamada literal por valor resuelve las dos cosas: cada clave se
 * ve entera, y cada texto en español es el respaldo de su t(). Son
 * dieciséis líneas; `test_perfumes.js` §1 exige que estén todas. */
const NOMBRES = {
  concentracion: {
    colonia: () => t("perfumes.concentracion.colonia", "Colonia"),
    edt: () => t("perfumes.concentracion.edt", "Eau de toilette"),
    edp: () => t("perfumes.concentracion.edp", "Eau de parfum"),
    parfum: () => t("perfumes.concentracion.parfum", "Parfum / extrait"),
  },
  familia: {
    citrico: () => t("perfumes.familia.citrico", "Cítrico"),
    fresco: () => t("perfumes.familia.fresco", "Fresco / acuático"),
    aromatico: () => t("perfumes.familia.aromatico", "Aromático / fougère"),
    amaderado: () => t("perfumes.familia.amaderado", "Amaderado"),
    ambar: () => t("perfumes.familia.ambar", "Ámbar / oriental"),
    floral: () => t("perfumes.familia.floral", "Floral"),
    chipre: () => t("perfumes.familia.chipre", "Chipre"),
    gourmand: () => t("perfumes.familia.gourmand", "Gourmand"),
    cuero: () => t("perfumes.familia.cuero", "Cuero"),
  },
  momento: {
    dia: () => t("perfumes.momento.dia", "Día"),
    noche: () => t("perfumes.momento.noche", "Noche"),
    ambos: () => t("perfumes.momento.ambos", "Día y noche"),
  },
};

const nombreDe = (grupo, v) => (v ? (NOMBRES[grupo][v] ? NOMBRES[grupo][v]() : v) : "");
export const nombreConcentracion = (v) => nombreDe("concentracion", v);
export const nombreFamilia = (v) => nombreDe("familia", v);
export const nombreMomento = (v) => nombreDe("momento", v);

/** Por debajo de esto, «se te está terminando». A ojo, como el nivel. */
export const UMBRAL_SE_TERMINA = 15;

/* Escrito sin `<=` a propósito: `test_i18n.js` §7 busca texto crudo
 * entre un `>` y un `<` de la misma línea, y una comparación con los
 * dos signos parece JSX. */
export const seTermina = (p) => Number.isFinite(p?.nivel) && !(p.nivel > UMBRAL_SE_TERMINA);

/**
 * Cuántos años lleva abierto. null si no se sabe.
 *
 * Un perfume abierto empieza a degradarse entre los tres y los cinco
 * años: el aire y la luz oxidan las notas de salida primero. Por eso
 * los dos avisos.
 */
export function anosAbierto(p, ahora = new Date()) {
  if (!p?.abierto_en) return null;
  const d = new Date(`${p.abierto_en}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return (ahora.getTime() - d.getTime()) / (365.25 * 24 * 3600 * 1000);
}

/** "cinco" | "tres" | null */
export function avisoApertura(p, ahora = new Date()) {
  const a = anosAbierto(p, ahora);
  if (a == null) return null;
  if (a >= 5) return "cinco";
  if (a >= 3) return "tres";
  return null;
}

/**
 * El perfume que se propone al registrar (decisión del dueño, 22/9):
 * el último que usaste; si todavía no usaste ninguno, el último que
 * cargaste. null si no hay perfumes.
 */
export function perfumeSugerido(perfumes) {
  const vivos = (perfumes || []).filter((p) => p && p.activo !== false);
  if (!vivos.length) return null;
  const usados = vivos.filter((p) => p.ultimo_uso);
  if (usados.length) {
    return usados.reduce((a, b) => (new Date(b.ultimo_uso) > new Date(a.ultimo_uso) ? b : a));
  }
  return vivos.reduce((a, b) => (new Date(b.creado_en || 0) > new Date(a.creado_en || 0) ? b : a));
}
